import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

/**
 * Left-side event log panel. Hidden on mobile unless `show` is true
 * (toggled from GameHeader's menu button). `memo`-wrapped so a state
 * update that doesn't change the event log slice + visibility +
 * close-callback skips this re-render.
 */
function EventLogPanel({ game, show, onClose, logEndRef }) {
  const { t } = useTranslation();
  return (
    <aside
      className={`${show ? "fixed inset-0 z-40 bg-black/30" : "hidden"} lg:relative lg:block lg:bg-transparent`}
    >
      <div
        className={`${show ? "w-72 h-full" : "w-64"} border-r border-[#1A237E]/10 dark:border-[#E3F2FD]/10 bg-white/90 dark:bg-slate-900/90 lg:bg-white/40 dark:bg-slate-900/40 flex flex-col`}
        data-testid="event-log-panel"
      >
        <div className="px-3 py-2 border-b border-[#1A237E]/10 dark:border-[#E3F2FD]/10 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
            {t("eventLog.title")}
          </h3>
          <Button variant="ghost" size="sm" className="lg:hidden p-1" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="overflow-y-auto scroll-thin p-2 space-y-1 max-h-[50vh] lg:max-h-[calc(100vh-14rem)]">
          {(game.event_log || []).map((ev, i) => (
            <div
              key={`log-${i}-${ev.type}`}
              className={`event-log-entry text-xs px-2 py-1.5 rounded-lg ${
                ev.type === "melee"
                  ? "bg-red-100 text-red-800"
                  : ev.type === "event"
                  ? "bg-amber-100 text-amber-800"
                  : ev.type === "system"
                  ? "bg-blue-100 text-blue-800"
                  : ev.type === "buy"
                  ? "bg-green-100 text-green-800"
                  : ev.type === "sell"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-white/60 dark:bg-slate-900/60 text-[#1A237E]/70 dark:text-[#E3F2FD]/70"
              }`}
            >
              {ev.message}
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>
    </aside>
  );
}

export default memo(EventLogPanel);
